import React, { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Briefcase } from "lucide-react";
import PersonnelNotifications from "./PersonnelNotifications";
import type {
  NotificationState,
  ConfirmDialogState,
} from "../hooks/candidate/usePersonnelAssignment";

interface AssignedKam {
  kam_user_id: number;
  kam_name?: string;
}

interface AvailableKam {
  id: number;
  username: string;
}

export interface KamAssignmentHookReturn {
  kams: AssignedKam[];
  availableKams: AvailableKam[];
  selectedKamId: string;
  setSelectedKamId: (id: string) => void;
  isAssigningKam: boolean;
  handleAssignKam: (overrideId?: string) => Promise<void>;
  handleRemoveKam: (kamUserId: number) => void;
  canManageKams: boolean;
  notification: NotificationState;
  closeNotification: () => void;
  confirmDialog: ConfirmDialogState;
  confirmRemoval: () => void;
  cancelRemoval: () => void;
}

interface KamAssignmentProps {
  hook: KamAssignmentHookReturn;
}

/**
 * Shared inline KAM (sales) assignment UI.
 * Used by both CandidateKamAssignment and ApplicationKamAssignment.
 */
const KamAssignment: React.FC<KamAssignmentProps> = ({ hook }) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const {
    kams,
    availableKams,
    selectedKamId,
    setSelectedKamId,
    isAssigningKam,
    handleAssignKam,
    handleRemoveKam,
    canManageKams,
    notification,
    closeNotification,
    confirmDialog,
    confirmRemoval,
    cancelRemoval,
  } = hook;

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedKamId(e.target.value);
    if (e.target.value) {
      handleAssignKam(e.target.value);
    }
  };

  return (
    <div className="tech-checker-inline">
      <span className="tech-checker-label">
        <Briefcase size={14} /> KAM:
      </span>
      {kams.length > 0 && (
        <div className="tech-checker-badges-inline">
          {kams.map((k) => (
            <span key={k.kam_user_id} className="tech-checker-badge-inline">
              {k.kam_name || t("common:misc.unknown")}
              {canManageKams && (
                <button
                  onClick={() => handleRemoveKam(k.kam_user_id)}
                  className="tech-checker-remove-inline"
                  title={t("common:actions.remove")}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {canManageKams && (
            <select
              value={selectedKamId}
              onChange={handleChange}
              className="tech-checker-add-select"
              title="Add KAM"
            >
              <option value="">+ {t("common:actions.add")}</option>
              {availableKams
                .filter((k) => !kams.some((ak) => ak.kam_user_id === k.id))
                .map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.username}
                  </option>
                ))}
            </select>
          )}
        </div>
      )}
      {kams.length === 0 && canManageKams && (
        <select
          value={selectedKamId}
          onChange={handleChange}
          className="tech-checker-add-select"
          disabled={isAssigningKam}
        >
          <option value="">
            {isAssigningKam
              ? t("common:loading.assigning")
              : `+ ${t("common:actions.assign")}`}
          </option>
          {availableKams.map((k) => (
            <option key={k.id} value={k.id}>
              {k.username}
            </option>
          ))}
        </select>
      )}
      {kams.length === 0 && !canManageKams && (
        <span className="tech-checker-badge-inline" style={{ opacity: 0.6 }}>
          {t("common:misc.none", "None")}
        </span>
      )}
      <PersonnelNotifications
        notification={notification}
        onCloseNotification={closeNotification}
        confirmDialog={confirmDialog}
        onConfirm={confirmRemoval}
        onCancel={cancelRemoval}
      />
    </div>
  );
};

export default KamAssignment;
